import { DataProps } from '@diamond/shared/types';
import * as SentryNext from '@sentry/nextjs';
import * as SentryReact from '@sentry/react';

// Helper function to determine error level based on error type
const getErrorLevel = (error: unknown): 'error' | 'warning' | 'info' => {
  const errorStr = JSON.stringify(error)?.toLowerCase();

  // Business logic errors should be warnings
  const businessErrors = [
    'min_order_not_reached',
    'all_item_cant_be_processed',
    'diff_price_or_stock',
    'partially_out_of_stock',
    'minimum order not met',
    'verifikasi data',
    'mohon menunggu saat ini data anda dalam proses verifikasi',
  ];

  if (businessErrors.some((err) => errorStr.includes(err))) {
    return 'warning';
  }

  // Network and timeout errors should be info level
  const networkErrors = [
    'failed to fetch',
    'network error',
    'timeout',
    'econnaborted',
    'err_network',
    'enotfound',
    'econnrefused',
    'etimedout',
  ];

  if (networkErrors.some((err) => errorStr.includes(err))) {
    return 'info';
  }

  // Default to error for unknown issues
  return 'error';
};

// Helper function to check if error should be ignored
const shouldIgnoreError = (error: unknown): boolean => {
  const errorStr = JSON.stringify(error)?.toLowerCase();

  const ignorePatterns = [
    'authorization token expired',
    'jwt_authorization_token_expired',
    'unauthorized',
    'invalid syntax for type',
    'cancel rendering',
    'metamask',
    'chunk',
    'handled',
    'request failed with status code 401',
    'request failed with status code 403',
  ];

  return ignorePatterns.some((pattern) => errorStr.includes(pattern));
};

export function adminCaptureException(
  response: DataProps,
  configureScopeCallback: (scope: SentryReact.Scope) => void
) {
  // Skip ignored errors to reduce noise
  if (shouldIgnoreError(response)) {
    return;
  }

  const errorLevel = getErrorLevel(response);

  return SentryReact.captureException(
    new Error(JSON.stringify(response)),
    (scope) => {
      // Set appropriate error level
      scope.setLevel(errorLevel);

      // Add context tags
      scope.setTag('app', 'sol-admin');
      scope.setTag('capture_method', 'adminCaptureException');
      scope.setTag('filtered_by', 'shared-utils');

      // Call user-provided configuration
      configureScopeCallback(scope as SentryReact.Scope);
      return scope;
    }
  );
}

export function buyerCaptureException(
  response: DataProps,
  configureScopeCallback: (
    scope: SentryNext.Scope,
    user?: SentryNext.User
  ) => void
) {
  // Skip ignored errors to reduce noise
  if (shouldIgnoreError(response)) {
    return;
  }

  const errorLevel = getErrorLevel(response);

  return SentryNext.captureException(
    new Error(JSON.stringify(response)),
    (scope) => {
      // Set appropriate error level
      scope.setLevel(errorLevel);

      // Add context tags
      scope.setTag('app', 'sol-buyer');
      scope.setTag('capture_method', 'buyerCaptureException');
      scope.setTag('filtered_by', 'shared-utils');

      // Get current user for callback
      const currentUser = scope.getUser();

      // Call user-provided configuration
      configureScopeCallback(scope as SentryNext.Scope, currentUser);
      return scope;
    }
  );
}
